import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { RefreshCw, Loader2, CheckCircle2, AlertTriangle, Users } from "lucide-react";

/**
 * HR sync configuration panel — toggle scheduled polling of zenith360-hr.com and manually trigger a pull.
 * Backend: GET /hr/sync-status, POST /hr/sync-config, POST /hr/sync-now, GET /hr/sync/employees
 */
export default function HRSyncPanel() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [interval, setIntervalMin] = useState(60);
  const [empCount, setEmpCount] = useState(0);

  const load = async () => {
    const [s, e] = await Promise.all([
      api.get("/hr/sync-status"),
      api.get("/hr/sync/employees").catch(() => ({ data: [] })),
    ]);
    setStatus(s.data);
    setEnabled(!!s.data.enabled);
    setIntervalMin(s.data.interval_minutes || 60);
    setEmpCount((e.data || []).length);
  };
  useEffect(() => { load(); }, []);

  const saveConfig = async () => {
    setBusy(true);
    try {
      await api.post("/hr/sync-config", { enabled, interval_minutes: interval });
      toast.success(`HR sync ${enabled ? "enabled" : "disabled"} · every ${interval} min`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/hr/sync-now");
      toast.success(`Synced ${r.data.synced} employees (${r.data.created} new)`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally { setSyncing(false); }
  };

  const s = status?.status || {};
  const configured = status?.base_url_configured;

  return (
    <div className="card-sharp p-5 space-y-4" data-testid="hr-sync-panel">
      <div className="flex items-center justify-between border-b pb-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-accent flex items-center gap-1.5">
            <Users className="w-3.5 h-3.5" /> HR Deep Sync · zenith360-hr.com
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            Auto-pulls employees on schedule so reimbursements, payroll postings and expense claims stay in
            sync with the HR portal.
          </div>
        </div>
        <div className={`text-[10px] font-mono uppercase px-2 py-1 border rounded-sm flex items-center gap-1 ${
            configured ? "text-emerald-600 bg-emerald-50 border-emerald-200"
                            : "text-amber-600 bg-amber-50 border-amber-200"}`}>
          {configured ? <><CheckCircle2 className="w-3 h-3" /> Endpoint configured</> :
                                 <><AlertTriangle className="w-3 h-3" /> HR_API_BASE_URL not set</>}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-xs">Auto-sync employees on schedule</Label>
            <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="hr-sync-enabled" />
          </div>
          <div>
            <Label className="text-xs">Interval (minutes) — min 15</Label>
            <Input type="number" min={15} max={1440} value={interval}
                     onChange={(e) => setIntervalMin(Math.max(15, Number(e.target.value)))}
                     className="rounded-sm mt-1 max-w-[160px]" data-testid="hr-sync-interval" />
          </div>
          <div className="flex gap-2">
            <Button onClick={saveConfig} disabled={busy} className="rounded-sm bg-accent hover:bg-accent/90 text-white"
                     data-testid="hr-save-config">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save schedule"}
            </Button>
            <Button variant="outline" onClick={syncNow} disabled={syncing} className="rounded-sm gap-1.5"
                     data-testid="hr-sync-now">
              {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RefreshCw className="w-4 h-4" /> Sync now</>}
            </Button>
          </div>
        </div>

        <div className="border-l pl-4 space-y-2 text-xs">
          <div className="text-[10px] font-mono uppercase text-muted-foreground">Last sync</div>
          {s.last_sync_at ? (
            <div className="space-y-1">
              <div>✅ <span className="font-mono">{new Date(s.last_sync_at).toLocaleString("en-IN")}</span></div>
              <div className="text-muted-foreground">Pulled {s.last_sync_count || 0} employees ({s.last_created || 0} new)</div>
              <div className="text-muted-foreground">Currently in system: <span className="font-mono font-medium">{empCount}</span> employees</div>
            </div>
          ) : (
            <div className="text-muted-foreground italic">Never synced yet — hit "Sync now" to test.</div>
          )}
          {s.last_error && (
            <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded-sm text-red-700 text-[11px]">
              <div className="font-mono text-[9px] uppercase mb-1">Last error</div>
              {s.last_error}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
